// src/components/UnifiedSummaryModal.jsx - Complete version with document summary support
// ============================================================
// CLIENT ID: Accepts clientId as prop, no hardcoded default
// Parent component MUST pass clientId from AuthContext
// ============================================================
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { API_BASE, apiFetch } from '../config/apiBase';
const SUMMARY_TYPES = {
  strategy: 'Strategic Priorities Summary',
  capabilities: 'Data & Analytics Capabilities Summary',
  readiness: 'Data Readiness & Governance Summary',
  document: 'Document Analysis Summary' // Added document type
};

const BRAND_BLUE = '#18365E';
const BRAND_ORANGE = '#FF6E4C';

const UnifiedSummaryModal = ({
  isOpen,
  onClose,
  // ============================================================
  // FIX: Changed default from '1' to null
  // Parent component MUST pass clientId from AuthContext
  // ============================================================
  clientId = null,
  summaryType = 'strategy',
}) => {
  const modalRef = useRef(null);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [regenerateCount, setRegenerateCount] = useState(0);
  const [metadata, setMetadata] = useState(null); // Added for document metadata

  // Data load - ENHANCED to handle document summaries
  const loadSummary = async () => {
    // Warn if clientId is missing
    if (!clientId) {
      console.warn('UnifiedSummaryModal: clientId prop is required');
      setError('Client ID is required to load summary');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      // Special handling for document summaries - retrieve from localStorage
      if (summaryType === 'document') {
        // Try the new key format first
        const docSummaryKey = `summary_${clientId}_document`;
        const storedSummary = localStorage.getItem(docSummaryKey);
        
        if (storedSummary) {
          try {
            const summaryData = JSON.parse(storedSummary);
            setSummary(summaryData.summary);
            setMetadata({
              generated_at: summaryData.generated_at,
              files_processed: summaryData.files_processed,
              title: summaryData.title || 'Document Analysis Summary',
              test_mode: summaryData.test_mode
            });
            setSurveyCompleted(true);
          } catch (e) {
            console.error('Error parsing document summary:', e);
            // Try fallback to old key format
            const fallbackKey = `docSummary_${clientId}_strategy`;
            const fallbackSummary = localStorage.getItem(fallbackKey);
            
            if (fallbackSummary) {
              try {
                const fallbackData = JSON.parse(fallbackSummary);
                setSummary(fallbackData.summary);
                setMetadata({
                  generated_at: fallbackData.generated_at,
                  files_processed: fallbackData.files_processed,
                  title: 'Document Analysis Summary',
                  test_mode: fallbackData.test_mode
                });
                setSurveyCompleted(true);
              } catch (fe) {
                console.error('Error parsing fallback summary:', fe);
                setError('Failed to load document summary');
              }
            } else {
              setSummary(null);
              setError('No document summary found. Please generate a summary from your uploaded documents.');
            }
          }
        } else {
          // Try fallback key if new key doesn't exist
          const fallbackKey = `docSummary_${clientId}_strategy`;
          const fallbackSummary = localStorage.getItem(fallbackKey);
          
          if (fallbackSummary) {
            try {
              const fallbackData = JSON.parse(fallbackSummary);
              setSummary(fallbackData.summary);
              setMetadata({
                generated_at: fallbackData.generated_at,
                files_processed: fallbackData.files_processed,
                title: 'Document Analysis Summary',
                test_mode: fallbackData.test_mode
              });
              setSurveyCompleted(true);
            } catch (e) {
              console.error('Error parsing fallback summary:', e);
              setError('Failed to load document summary');
            }
          } else {
            setSummary(null);
            setError('No document summary found. Please generate a summary from your uploaded documents.');
          }
        }
        setIsLoading(false);
        return;
      }
      
      // Regular survey summaries - existing logic
      const response = await apiFetch(`${API_BASE}/api/summary/${clientId}/${summaryType}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (response.status === 404) {
        setSummary(null);
        setSurveyCompleted(true);
        setIsLoading(false);
        return;
      }
      if (!response.ok) throw new Error(`Failed to load summary: ${response.status}`);

      const data = await response.json();
      const summaryContent = data.summary || data.content || data.summary_md || data;
      if (typeof summaryContent === 'string') {
        setSummary(summaryContent);
        setSurveyCompleted(true);
        // Set metadata for regular summaries if available
        if (data.metadata) {
          setMetadata(data.metadata);
        }
      } else {
        setSummary(null);
      }
    } catch (err) {
      console.error('Error loading summary:', err);
      if (!String(err.message || '').includes('404')) setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate - ENHANCED to handle document summaries
  const generateSummary = async () => {
    if (!clientId) {
      setError('Client ID is required to generate summary');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    try {
      // For document summaries, we should not regenerate from here
      // The user should use the regenerate button on the HomePage
      if (summaryType === 'document') {
        setError('Please use the "Regenerate Strategy Summary" button on the main page to regenerate document summaries.');
        setIsGenerating(false);
        return;
      }
      
      // Regular survey summary generation - existing logic
      // Note: Backend expects clientId as string, not integer
      const response = await apiFetch(`${API_BASE}/api/summary/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientId: clientId,
            summaryType,
            userId: 'abc123',
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to generate summary: ${response.status}`);
      }
      const data = await response.json();
      if (data.success && data.summary) {
        setSummary(data.summary);
        setRegenerateCount((prev) => prev + 1);
      } else {
        throw new Error('Generated summary is empty');
      }
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadSummary();
  }, [isOpen, clientId, summaryType]);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => { if (e.key === 'Escape' && !isGenerating) onClose?.(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, isGenerating, onClose]);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget && !isGenerating) onClose?.();
  };

  const handleExportPDF = () => {
    console.log('Export PDF functionality to be implemented');
  };

  // Enhanced markdown renderer
  const renderFormattedSummary = (text) => {
    if (!text) return null;
    
    const lines = String(text).split('\n');
    const elements = [];
    let currentList = null;
    let currentSubList = null;
    let inSection = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      // Skip empty lines
      if (!trimmed) {
        // Close any open lists
        if (currentSubList) {
          currentList.push(currentSubList);
          currentSubList = null;
        }
        if (currentList) {
          elements.push(
            <ul key={`list-${i}`} className="space-y-3 my-4">
              {currentList}
            </ul>
          );
          currentList = null;
        }
        continue;
      }
      
      // H1 - Main title (# )
      if (trimmed.startsWith('# ')) {
        // Close any open lists
        if (currentSubList) {
          currentList.push(currentSubList);
          currentSubList = null;
        }
        if (currentList) {
          elements.push(
            <ul key={`list-pre-${i}`} className="space-y-3 my-4">
              {currentList}
            </ul>
          );
          currentList = null;
        }
        
        elements.push(
          <h1 
            key={i} 
            className="text-2xl font-bold mb-6 pb-3 border-b-2"
            style={{ color: BRAND_BLUE, borderColor: BRAND_ORANGE }}
          >
            {trimmed.substring(2)}
          </h1>
        );
        inSection = false;
      }
      // H2 - Section headers (## )
      else if (trimmed.startsWith('## ')) {
        // Close any open lists
        if (currentSubList) {
          currentList.push(currentSubList);
          currentSubList = null;
        }
        if (currentList) {
          elements.push(
            <ul key={`list-pre-${i}`} className="space-y-3 my-4">
              {currentList}
            </ul>
          );
          currentList = null;
        }
        
        elements.push(
          <h2 
            key={i} 
            className="text-xl font-semibold mt-8 mb-4 flex items-center"
            style={{ color: BRAND_BLUE }}
          >
            <span 
              className="inline-block w-1 h-6 mr-3"
              style={{ backgroundColor: BRAND_ORANGE }}
            />
            {trimmed.substring(3)}
          </h2>
        );
        inSection = true;
      }
      // H3 - Subsection headers (### )
      else if (trimmed.startsWith('### ')) {
        // Close any open lists
        if (currentSubList) {
          currentList.push(currentSubList);
          currentSubList = null;
        }
        if (currentList) {
          elements.push(
            <ul key={`list-pre-${i}`} className="space-y-3 my-4">
              {currentList}
            </ul>
          );
          currentList = null;
        }
        
        elements.push(
          <h3 
            key={i} 
            className="text-lg font-semibold mt-6 mb-3"
            style={{ color: '#2D3748' }}
          >
            {trimmed.substring(4)}
          </h3>
        );
      }
      // Bullet points (- or • or * at start)
      else if (/^[-•*]\s+/.test(trimmed)) {
        const content = trimmed.replace(/^[-•*]\s+/, '');
        
        // Check if this is a sub-bullet (starts with more spaces)
        const isSubBullet = line.match(/^\s{2,}[-•*]\s+/);
        
        if (isSubBullet) {
          // This is a sub-bullet
          if (!currentSubList) {
            currentSubList = [];
          }
          currentSubList.push(
            <li key={`sub-${i}`} className="flex items-start gap-2 ml-6">
              <span 
                className="inline-block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                style={{ backgroundColor: '#CBD5E0' }}
              />
              <span className="text-gray-700 text-sm">{renderInlineFormatting(content)}</span>
            </li>
          );
        } else {
          // This is a main bullet
          if (!currentList) currentList = [];
          
          // Add any pending sub-list
          if (currentSubList) {
            currentList.push(
              <ul key={`sublist-${i}`} className="space-y-2 mt-2">
                {currentSubList}
              </ul>
            );
            currentSubList = null;
          }
          
          currentList.push(
            <li key={`bullet-${i}`} className="flex items-start gap-3">
              <span 
                className="inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                style={{ backgroundColor: BRAND_ORANGE }}
              />
              <span className="text-gray-800">{renderInlineFormatting(content)}</span>
            </li>
          );
        }
      }
      // Regular paragraph
      else {
        // Close any open lists first
        if (currentSubList) {
          currentList.push(currentSubList);
          currentSubList = null;
        }
        if (currentList) {
          elements.push(
            <ul key={`list-${i}`} className="space-y-3 my-4">
              {currentList}
            </ul>
          );
          currentList = null;
        }
        
        // Check if this looks like a key-value pair (contains ":")
        if (trimmed.includes(':') && !trimmed.startsWith('http')) {
          const colonIndex = trimmed.indexOf(':');
          const label = trimmed.substring(0, colonIndex);
          const value = trimmed.substring(colonIndex + 1).trim();
          
          elements.push(
            <div key={i} className="my-3">
              <span className="font-semibold" style={{ color: BRAND_BLUE }}>
                {label}:
              </span>
              <span className="ml-2 text-gray-800">
                {renderInlineFormatting(value)}
              </span>
            </div>
          );
        } else {
          elements.push(
            <p key={i} className="text-gray-800 my-4 leading-relaxed">
              {renderInlineFormatting(trimmed)}
            </p>
          );
        }
      }
    }
    
    // Close any remaining open lists
    if (currentSubList) {
      currentList.push(currentSubList);
    }
    if (currentList) {
      elements.push(
        <ul key="list-final" className="space-y-3 my-4">
          {currentList}
        </ul>
      );
    }
    
    return <div className="space-y-2">{elements}</div>;
  };

  // Helper to render inline formatting (bold, italic, code)
  const renderInlineFormatting = (text) => {
    if (!text) return text;
    
    // Split by bold markers (**)
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Bold text
        const boldText = part.slice(2, -2);
        return (
          <strong 
            key={idx} 
            className="font-semibold"
            style={{ 
              color: BRAND_BLUE,
              background: 'linear-gradient(180deg, transparent 60%, #FFE5D9 60%)',
              padding: '0 2px'
            }}
          >
            {boldText}
          </strong>
        );
      }
      
      // Check for numbers that look like metrics (e.g., "90 days", "$2M", "3-5 days", "40%")
      const metricPattern = /(\d+[-–]\d+|\$[\d.]+[MKB]?|\d+%|\d+\s+(?:days?|months?|years?|hours?))/g;
      if (metricPattern.test(part)) {
        return part.split(metricPattern).map((segment, i) => {
          if (metricPattern.test(segment)) {
            return (
              <span 
                key={`${idx}-${i}`}
                className="inline-block px-2 py-0.5 rounded text-sm font-medium"
                style={{ 
                  backgroundColor: '#F7FAFC',
                  border: '1px solid #E2E8F0',
                  color: BRAND_BLUE
                }}
              >
                {segment}
              </span>
            );
          }
          return segment;
        });
      }
      
      return part;
    });
  };

  if (!isOpen) return null;

  // Ensure a portal root exists
  let root = document.getElementById('summary-modal-root');
  if (!root) {
    root = document.createElement('div');
    root.id = 'summary-modal-root';
    document.body.appendChild(root);
  }

  const title = SUMMARY_TYPES[summaryType] || 'Summary';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={handleBackdrop}
      className="fixed inset-0 z-[9999] grid place-items-center p-4 bg-black/50 backdrop-blur-sm"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl ring-1 ring-black/5 flex flex-col"
      >
        {/* Header - ENHANCED for document summaries */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-8 py-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight" style={{ color: BRAND_BLUE }}>
              {title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {clientId ? `Client ${clientId}` : 'No client selected'} • {
                summaryType === 'document' && metadata ? 
                  `${metadata.files_processed || 0} documents analyzed` : 
                  summary ? 'Generated by AI' : 'Ready to generate'
              }
              {metadata?.test_mode && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                  Test Mode
                </span>
              )}
            </p>
          </div>
          <button
            aria-label="Close summary"
            onClick={onClose}
            disabled={isGenerating}
            className="w-10 h-10 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-8 bg-gray-50">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div
                  className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-t-transparent"
                  style={{ borderColor: BRAND_ORANGE }}
                />
                <p className="mt-4 text-sm text-gray-500">Loading summary…</p>
              </div>
            </div>
          )}

          {isGenerating && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div
                  className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-t-transparent"
                  style={{ borderColor: BRAND_BLUE }}
                />
                <p className="mt-4 text-sm text-gray-600 font-medium">Generating summary with AI…</p>
                <p className="text-xs text-gray-500 mt-1">This may take 30-45 seconds</p>
              </div>
            </div>
          )}

          {error && !isGenerating && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!isLoading && !isGenerating && !error && summary && (
            <div className="bg-white rounded-xl p-8 shadow-sm ring-1 ring-black/5">
              {/* Document metadata display */}
              {summaryType === 'document' && metadata && (
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Generated:</span> {
                        metadata.generated_at ? 
                          new Date(metadata.generated_at).toLocaleString() : 
                          'Recently'
                      }
                    </div>
                    <div>
                      <span className="font-medium">Documents Analyzed:</span> {
                        metadata.files_processed || 'Unknown'
                      }
                    </div>
                  </div>
                </div>
              )}
              
              {renderFormattedSummary(summary)}
              
              {/* Regenerate button for survey summaries only */}
              {summaryType !== 'document' && regenerateCount > 0 && regenerateCount < 3 && (
                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-center">
                  <button
                    onClick={generateSummary}
                    className="text-sm font-medium transition-colors"
                    style={{ color: BRAND_BLUE }}
                  >
                    Regenerate Summary ({3 - regenerateCount} attempts remaining)
                  </button>
                </div>
              )}
            </div>
          )}

          {!isLoading && !isGenerating && !error && !summary && summaryType !== 'document' && (
            <div className="text-center py-12">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6 max-w-md mx-auto">
                <p className="text-yellow-800 font-medium mb-1">No summary available yet</p>
                <p className="text-yellow-700 text-sm">
                  Survey completed. Click below to generate an AI summary.
                </p>
              </div>
              <button
                onClick={generateSummary}
                disabled={!clientId}
                className="px-6 py-3 text-white rounded-lg hover:opacity-95 transition-colors font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: BRAND_ORANGE }}
              >
                Generate Summary
              </button>
            </div>
          )}

          {!isLoading && !isGenerating && !error && !summary && summaryType === 'document' && (
            <div className="text-center py-12">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6 max-w-md mx-auto">
                <p className="text-yellow-800 font-medium mb-1">No document summary available</p>
                <p className="text-yellow-700 text-sm">
                  Please generate a summary from the Document Upload section on the main page.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer - ENHANCED for document summaries */}
        <div className="px-8 py-4 border-t border-gray-200 bg-white flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {!isLoading && !isGenerating && summary && (
              summaryType === 'document' ? 
                'Summary generated from uploaded documents' : 
                'Summary generated from survey responses'
            )}
          </p>
          <div className="flex gap-3">
            {!isLoading && !isGenerating && summary && (
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2"
                style={{ color: BRAND_BLUE, borderColor: BRAND_BLUE }}
              >
                Export PDF
              </button>
            )}
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="px-5 py-2 text-sm font-medium text-white rounded-lg hover:opacity-95 transition-colors focus:outline-none focus-visible:ring-2 disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    root
  );
};

export default UnifiedSummaryModal;